import clsx from "clsx";
import { type ComponentRef, forwardRef, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getResourceIRI, useLanguageListQuery } from "@sol/sdk";
import { Select, type SelectProps } from "@sol/uikit/data-entry/Select/Select";

type Props = {} & Omit<SelectProps, "options">;

type LanguageFlagProps = {
    locale: string;
};

const LanguageFlag = ({ locale }: LanguageFlagProps) => {
    const [hasError, setHasError] = useState(false);

    if (hasError) {
        return null;
    }

    return (
        <img
            src={`/static/flags/${locale.substring(0, 2)}.svg`}
            alt=""
            className="size-6 shrink-0"
            onError={() => setHasError(true)}
        />
    );
};

export const LanguageSelect = forwardRef<ComponentRef<typeof Select>, Props>(function LanguageSelect(
    { className, placeholder, ...rest },
    ref,
) {
    const [t] = useTranslation();
    const { data: languages, isLoading } = useLanguageListQuery({});

    const languageOptions = useMemo(
        () =>
            languages?.["hydra:member"]?.map(language => {
                const { title, locale } = language;

                return {
                    value: getResourceIRI(language),
                    label: title,
                    locale: locale,
                };
            }) ?? [],
        [languages],
    );

    return (
        <Select
            options={languageOptions}
            className={clsx("min-w-40", className)}
            loading={isLoading}
            ref={ref}
            labelRender={option => {
                let locale: string | undefined = option.value?.toString();

                locale = languageOptions.find(o => o.value === option.value)?.locale;

                if (!locale) {
                    return (
                        <span className="ant-select-selection-placeholder">
                            {placeholder ?? t("component.shared.LanguageSelect.placeholder")}
                        </span>
                    );
                }

                return (
                    <span lang={locale} className="inline-flex items-center gap-ant-xs align-middle">
                        {locale ? <LanguageFlag locale={locale} /> : null}
                        <span className="flex-1 overflow-hidden text-ellipsis">{option.label}</span>
                    </span>
                );
            }}
            optionRender={({ data: option }) => (
                <span lang={option.locale} className="inline-flex gap-ant-xs align-middle">
                    <LanguageFlag locale={option.locale} />
                    <span className="flex-1 overflow-hidden text-ellipsis">{option.label}</span>
                </span>
            )}
            {...rest}
        />
    );
});

export default LanguageSelect;
